'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroConnection, HeroState } from './utils';
import type { InsightResponse } from 'src/api/responses';
import type React from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import useInsights from 'src/hooks/useInsights';
import { FadeInOnScroll, HERO_MOTION } from './motion';
interface HeroInsightsProps {
  heroState: HeroState;
  connections: HeroConnection[];
  insightsUrl: string;
  connectionsUrl: string;
  onNavigate: (url: string) => void;
}
const MAX_INSIGHTS = 3;
const TEASERS = [{
  title: 'Spot budget opportunities',
  body: 'See where spend can move to create more impact.'
}, {
  title: 'Understand what changed',
  body: 'Turn channel movement into a clear business story.'
}, {
  title: 'Know what to do next',
  body: 'Get focused recommendations ranked by potential.'
}];
const PRIORITY_ORDER: Record<InsightResponse['priority'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3
};
const Row = styled('article')(({
  theme
}) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: theme.sizes.spacing['07'],
  padding: `${theme.sizes.spacing['08']} 0`,
  borderTop: `${theme.sizes.borderWidth[1]} solid ${theme.colors.border.secondary}`,
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'start',
    gap: theme.sizes.spacing['09']
  }
}));
const rank = (index: number) => `${index + 1}`.padStart(2, '0');
const RankLabel: React.FC<{
  index: number;
}> = t0 => {
  const $ = _c(4);
  const {
    index
  } = t0;
  let t1;
  if ($[0] !== index) {
    t1 = rank(index);
    $[0] = index;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Typography variant="body-sm" sx={_temp}>{t1}</Typography>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const getImpact = (insight: InsightResponse) => {
  const metric = insight.key_metrics[0];
  return metric ? `${metric.key}: ${metric.value}` : null;
};
const sortInsights = (insights: InsightResponse[]) => [...insights].filter(insight => !insight.handled).sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]).slice(0, MAX_INSIGHTS);
const HeroInsights: React.FC<HeroInsightsProps> = t0 => {
  const $ = _c(30);
  const {
    heroState,
    connections,
    insightsUrl,
    connectionsUrl,
    onNavigate
  } = t0;
  const {
    insights,
    isLoading
  } = useInsights();
  let t1;
  if ($[0] !== connections || $[1] !== connectionsUrl || $[2] !== heroState || $[3] !== insights || $[4] !== insightsUrl || $[5] !== isLoading || $[6] !== onNavigate) {
    const topInsights = sortInsights(insights);
    let T0;
    let insightRow;
    let t2;
    let t3;
    let t4;
    let t5;
    let t6;
    let t7;
    if ($[8] !== connections || $[9] !== connectionsUrl || $[10] !== heroState || $[11] !== isLoading || $[12] !== onNavigate) {
      const lostConnections = connections.filter(_temp2);
      const {
        insightRow: t8,
        teaserCard
      } = HERO_MOTION;
      insightRow = t8;
      const showSkeleton = heroState === "loading" || heroState === "normal" && isLoading;
      T0 = FlexColumn;
      t2 = "section";
      t3 = _temp3;
      let t9;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <Typography variant="heading-xl" component="h2" sx={_temp5}>AI Insights</Typography>;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      let t10;
      if ($[22] !== heroState) {
        t10 = heroState === "normal" && <Chip size="small" color="primary" variant="outlined" label="AI recommended" />;
        $[22] = heroState;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      if ($[24] !== t10) {
        t4 = <FlexRow sx={_temp4}>{t9}{t10}</FlexRow>;
        $[24] = t10;
        $[25] = t4;
      } else {
        t4 = $[25];
      }
      if ($[26] !== showSkeleton) {
        t5 = showSkeleton && <FlexColumn aria-hidden={true}>{Array.from({
            length: MAX_INSIGHTS
          }, _temp7)}</FlexColumn>;
        $[26] = showSkeleton;
        $[27] = t5;
      } else {
        t5 = $[27];
      }
      if ($[28] !== heroState) {
        t6 = heroState === "no-connections" && <FlexColumn sx={_temp8}><FlexRow sx={_temp9}><Typography variant="body-sm" sx={_temp10}>AI insights start with your first connection — daily recommendations based on your spend, leads and traffic.</Typography></FlexRow><FlexColumn>{TEASERS.map((teaser, index_0) => <FadeInOnScroll key={teaser.title} y={teaserCard.y} duration={teaserCard.duration} delay={index_0 * teaserCard.stagger}><Row><RankLabel index={index_0} /><FlexColumn sx={_temp11}><Typography variant="heading-sm" component="h3" sx={_temp12}>{teaser.title}</Typography><Typography variant="body-xs" sx={_temp13}>AI preview</Typography><Typography variant="body-sm" sx={_temp14}>{teaser.body}</Typography></FlexColumn><Chip size="small" variant="outlined" label="Preview" /></Row></FadeInOnScroll>)}</FlexColumn></FlexColumn>;
        $[28] = heroState;
        $[29] = t6;
      } else {
        t6 = $[29];
      }
      t7 = heroState === "no-data" && <FlexColumn sx={_temp15}><Typography variant="body-sm" sx={_temp16}>Your first insights are being prepared — Effektify reviews spend, leads and traffic as soon as data lands.</Typography><FlexColumn>{lostConnections.map((connection_0, index_1) => <FadeInOnScroll key={connection_0.id} y={insightRow.y} duration={insightRow.duration} delay={index_1 * insightRow.stagger}><Row><RankLabel index={index_1} /><FlexColumn sx={_temp17}><Typography variant="heading-sm" component="h3" sx={_temp18}>{`${connection_0.name} lost its connection`}</Typography><Typography variant="body-xs" sx={_temp19}>Insights paused for this platform</Typography><Typography variant="body-sm" sx={_temp20}>Reconnect to keep data — and daily insights — coming.</Typography></FlexColumn><Button variant="outlined" size="small" onClick={() => onNavigate(connectionsUrl)}>Reconnect</Button></Row></FadeInOnScroll>)}</FlexColumn></FlexColumn>;
      $[8] = connections;
      $[9] = connectionsUrl;
      $[10] = heroState;
      $[11] = isLoading;
      $[12] = onNavigate;
      $[13] = T0;
      $[14] = insightRow;
      $[15] = t2;
      $[16] = t3;
      $[17] = t4;
      $[18] = t5;
      $[19] = t6;
      $[20] = t7;
    } else {
      T0 = $[13];
      insightRow = $[14];
      t2 = $[15];
      t3 = $[16];
      t4 = $[17];
      t5 = $[18];
      t6 = $[19];
      t7 = $[20];
    }
    t1 = <T0 component={t2} sx={t3}>{t4}{t5}{t6}{t7}{heroState === "normal" && !isLoading && (topInsights.length === 0 ? <Typography variant="body-sm" sx={_temp21}>No new insights right now. We'll let you know when something needs your attention.</Typography> : <FlexColumn>{topInsights.map((insight, index_2) => <FadeInOnScroll key={insight.id} y={insightRow.y} duration={insightRow.duration} delay={index_2 * insightRow.stagger}><Row><RankLabel index={index_2} /><FlexColumn sx={_temp22}><Typography variant="heading-lg" component="h3" sx={_temp23}>{insight.title}</Typography>{getImpact(insight) && <Typography variant="body-sm" sx={_temp24}>{getImpact(insight)}</Typography>}<Typography variant="body1" sx={_temp25}>{insight.text}</Typography></FlexColumn><Button variant="text" size="small" onClick={() => onNavigate(insightsUrl)}>Read more</Button></Row></FadeInOnScroll>)}</FlexColumn>)}</T0>;
    $[0] = connections;
    $[1] = connectionsUrl;
    $[2] = heroState;
    $[3] = insights;
    $[4] = insightsUrl;
    $[5] = isLoading;
    $[6] = onNavigate;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
export default HeroInsights;
function _temp(theme) {
  return {
    color: theme.colors.text.tertiary
  };
}
function _temp2(connection) {
  return connection.status === "lost";
}
function _temp3(theme) {
  return {
    gap: theme.sizes.spacing["08"]
  };
}
function _temp4(theme_0) {
  return {
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme_0.sizes.spacing["05"]
  };
}
function _temp5(theme_1) {
  return {
    color: theme_1.colors.text.primary
  };
}
function _temp6(theme_2) {
  return {
    gap: theme_2.sizes.spacing["04"]
  };
}
function _temp7(_, index) {
  return <Row key={index}><RankLabel index={index} /><FlexColumn sx={_temp6}><Skeleton variant="text" width={224} height={28} /><Skeleton variant="text" width={160} /><Skeleton variant="text" width="100%" sx={{
        maxWidth: "28rem"
      }} /></FlexColumn><Skeleton variant="rounded" width={112} height={36} /></Row>;
}
function _temp8(theme_3) {
  return {
    gap: theme_3.sizes.spacing["07"]
  };
}
function _temp9(theme_4) {
  return {
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme_4.sizes.spacing["07"]
  };
}
function _temp10(theme_5) {
  return {
    color: theme_5.colors.text.tertiary,
    maxWidth: "36rem"
  };
}
function _temp11(theme_6) {
  return {
    gap: theme_6.sizes.spacing["02"]
  };
}
function _temp12(theme_7) {
  return {
    color: theme_7.colors.text.primary
  };
}
function _temp13(theme_8) {
  return {
    color: theme_8.colors.text.brand
  };
}
function _temp14(theme_9) {
  return {
    color: theme_9.colors.text.secondary
  };
}
function _temp15(theme_10) {
  return {
    gap: theme_10.sizes.spacing["07"]
  };
}
function _temp16(theme_11) {
  return {
    color: theme_11.colors.text.tertiary
  };
}
function _temp17(theme_12) {
  return {
    gap: theme_12.sizes.spacing["02"]
  };
}
function _temp18(theme_13) {
  return {
    color: theme_13.colors.text.primary
  };
}
function _temp19(theme_14) {
  return {
    color: theme_14.colors.text.error
  };
}
function _temp20(theme_15) {
  return {
    color: theme_15.colors.text.secondary
  };
}
function _temp21(theme_16) {
  return {
    color: theme_16.colors.text.tertiary
  };
}
function _temp22(theme_17) {
  return {
    gap: theme_17.sizes.spacing["02"]
  };
}
function _temp23(theme_18) {
  return {
    color: theme_18.colors.text.primary
  };
}
function _temp24(theme_19) {
  return {
    color: theme_19.colors.text["trend-down"]
  };
}
function _temp25(theme_20) {
  return {
    color: theme_20.colors.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  };
}